import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { AccountBookOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { downloadProtectedFile, get, post } from '../../api/client';

const { Text } = Typography;

interface MonthlySlice {
  month: string;
  orders: number;
  income: number;
  expense: number;
  incomePending: number;
  expensePending: number;
}

interface ArchiveRow {
  id: string;
  year: number;
  orderCount: number;
  incomeTotal: number;
  expenseTotal: number;
  generatedAt: string;
  backupAt: string | null;
  backupNote: string | null;
  purgedAt: string | null;
  purgedCount: number;
  detail: { months: MonthlySlice[]; settledOrders: number; unsettledOrders: number };
}

/** 清理预览/执行的结果：闸门判定与条数都在 data 里，message 会被管理端的解包丢掉 */
interface PurgeReport {
  year: number;
  eligible: number;
  deleted: number;
  filesRecycled: number;
  kept: number;
  keptReasons: string[];
  gaps: string[];
}

const money = (value: number) => `¥${Number(value || 0).toFixed(2)}`;
const stamp = (value: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—');

/** 台账导出走的是带 JWT 的 GET，不是 apiClient，所以 URL 要自己拼上 /api 前缀。 */
const exportUrl = (year: number) => `/api/admin/annual-archives/${year}/export`;

export default function AnnualArchivePage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateYear, setGenerateYear] = useState<number | null>(null);
  const [backupTarget, setBackupTarget] = useState<ArchiveRow | null>(null);
  const [backupNote, setBackupNote] = useState('');
  const [purgeTarget, setPurgeTarget] = useState<ArchiveRow | null>(null);
  const [purgeConfirmed, setPurgeConfirmed] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['annual-archives'],
    queryFn: () => get<ArchiveRow[]>('/admin/annual-archives'),
  });

  const rows = data ?? [];

  /** 只在弹窗打开时取预览：闸门是否满足必须当场告诉管理员，不能让他猜。 */
  const { data: purgePreview, isFetching: previewing } = useQuery({
    queryKey: ['annual-archives', 'purge-preview', purgeTarget?.year],
    queryFn: () => get<PurgeReport>(`/admin/annual-archives/${purgeTarget?.year}/purge-preview`),
    enabled: purgeTarget !== null,
  });

  const generate = useMutation({
    mutationFn: (year: number) => post<ArchiveRow>('/admin/annual-archives', { year }),
    onSuccess: (archive) => {
      message.success(`${archive.year} 年归档报表已生成，共 ${archive.orderCount} 单`);
      setGenerateOpen(false);
      setGenerateYear(null);
      void queryClient.invalidateQueries({ queryKey: ['annual-archives'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '生成年度报表失败'),
  });

  const confirmBackup = useMutation({
    mutationFn: ({ year, note }: { year: number; note: string }) =>
      post<ArchiveRow>(`/admin/annual-archives/${year}/backup`, { note }),
    onSuccess: (archive) => {
      message.success(`${archive.year} 年备份已确认`);
      setBackupTarget(null);
      setBackupNote('');
      void queryClient.invalidateQueries({ queryKey: ['annual-archives'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '确认备份失败'),
  });

  const purge = useMutation({
    mutationFn: (year: number) => post<PurgeReport>(`/admin/annual-archives/${year}/purge`, { confirmed: true }),
    onSuccess: (result) => {
      message.success(
        `${result.year} 年清理完成：删除 ${result.deleted} 条，回收凭证文件 ${result.filesRecycled} 个，保留 ${result.kept} 条`,
      );
      setPurgeTarget(null);
      setPurgeConfirmed(false);
      void queryClient.invalidateQueries({ queryKey: ['annual-archives'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '按年清理失败'),
  });

  const download = useMutation({
    mutationFn: (year: number) => downloadProtectedFile(exportUrl(year), `annual-archive-${year}.xlsx`),
    onError: (e) => message.error(e instanceof Error ? e.message : '导出台账失败'),
  });

  const columns: TableProps<ArchiveRow>['columns'] = [
    { title: '年度', dataIndex: 'year', width: 80, render: (v: number) => <Text strong>{v}</Text> },
    { title: '订单数', dataIndex: 'orderCount', width: 90 },
    {
      title: '收入合计',
      dataIndex: 'incomeTotal',
      width: 130,
      render: (v: number) => <Text style={{ color: '#16a34a' }}>{money(v)}</Text>,
    },
    {
      title: '支出合计',
      dataIndex: 'expenseTotal',
      width: 130,
      render: (v: number) => <Text style={{ color: '#dc2626' }}>{money(v)}</Text>,
    },
    {
      title: '结清情况',
      key: 'settle',
      width: 150,
      render: (_, r: ArchiveRow) => `${r.detail.settledOrders} 已结清 / ${r.detail.unsettledOrders} 未结清`,
    },
    { title: '生成时间', dataIndex: 'generatedAt', width: 140, render: stamp },
    {
      title: '备份',
      key: 'backup',
      width: 120,
      render: (_, r: ArchiveRow) =>
        r.backupAt ? (
          <Tag color="green" title={r.backupNote ?? undefined}>
            已确认
          </Tag>
        ) : (
          <Tag color="orange">未确认</Tag>
        ),
    },
    {
      title: '清理',
      key: 'purge',
      width: 130,
      render: (_, r: ArchiveRow) =>
        r.purgedAt ? (
          <Text>已清理 {r.purgedCount} 条</Text>
        ) : (
          <Text type="secondary">未清理</Text>
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, r: ArchiveRow) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={download.isPending && download.variables === r.year}
            onClick={() => download.mutate(r.year)}
          >
            导出台账
          </Button>
          {!r.purgedAt && (
            <>
              <Button
                size="small"
                disabled={!!r.backupAt}
                onClick={() => {
                  setBackupTarget(r);
                  setBackupNote(r.backupNote ?? '');
                }}
              >
                确认备份
              </Button>
              <Button size="small" danger onClick={() => { setPurgeTarget(r); setPurgeConfirmed(false); }}>
                按年清理
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const purgeBlocked = (purgePreview?.gaps ?? []).length > 0;

  return (
    <Card
      title="年度归档台账"
      extra={
        <Space>
          <Button
            type="primary"
            icon={<AccountBookOutlined />}
            onClick={() => {
              setGenerateYear(dayjs().subtract(1, 'year').year());
              setGenerateOpen(true);
            }}
          >
            生成年度报表
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['annual-archives'] })} />
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title="按年清理的四道闸门"
        description="年度已结束 → 报表已生成 → 报表已确认另存备份 → 单笔满 1 年且已结清。四条缺任何一条都不会删除订单；删除不可恢复，报表生成后请先导出台账并另存，再确认备份。"
      />
      <Table<ArchiveRow>
        rowKey="id"
        loading={isLoading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1330 }}
        pagination={false}
        locale={{ emptyText: '还没有生成过年度报表，点击右上角「生成年度报表」出第一年台账' }}
        expandable={{
          rowExpandable: (r) => r.detail.months.length > 0,
          expandedRowRender: (r) => (
            <Table<MonthlySlice>
              rowKey="month"
              size="small"
              pagination={false}
              dataSource={r.detail.months}
              columns={[
                { title: '月份', dataIndex: 'month', width: 100 },
                { title: '订单数', dataIndex: 'orders', width: 90 },
                { title: '收入', dataIndex: 'income', width: 120, render: money },
                { title: '支出', dataIndex: 'expense', width: 120, render: money },
                { title: '待收款(收入侧)', dataIndex: 'incomePending', width: 120, render: money },
                { title: '待付款(支出侧)', dataIndex: 'expensePending', width: 120, render: money },
              ]}
            />
          ),
        }}
      />

      <Modal
        title="生成年度报表"
        open={generateOpen}
        onCancel={() => setGenerateOpen(false)}
        onOk={() => generateYear && generate.mutate(generateYear)}
        okText="生成"
        confirmLoading={generate.isPending}
        okButtonProps={{ disabled: !generateYear }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">只能归档已经过完的年度；已按年清理过的年度不可重算。</Text>
          <InputNumber
            min={2023}
            max={dayjs().year()}
            precision={0}
            style={{ width: 160 }}
            value={generateYear}
            onChange={(v) => setGenerateYear(typeof v === 'number' ? v : null)}
            placeholder="年份"
          />
        </Space>
      </Modal>

      <Modal
        title={`确认 ${backupTarget?.year ?? ''} 年备份`}
        open={backupTarget !== null}
        onCancel={() => setBackupTarget(null)}
        onOk={() => backupTarget && confirmBackup.mutate({ year: backupTarget.year, note: backupNote.trim() })}
        okText="确认已另存备份"
        confirmLoading={confirmBackup.isPending}
        okButtonProps={{ disabled: !backupNote.trim() }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">
            填写备份的实际存放位置（如「公司 NAS / 财务备份盘 / 网盘目录」）。确认后台账表格文件与这份说明是一起留存的，
            日后按年清理才找得回来；此处只是声明，系统不会代你把文件存到任何地方。
          </Text>
          <Input.TextArea
            rows={3}
            maxLength={200}
            showCount
            placeholder="备份存放位置（必填）"
            value={backupNote}
            onChange={(e) => setBackupNote(e.target.value)}
          />
        </Space>
      </Modal>

      <Modal
        title={`按年清理 ${purgeTarget?.year ?? ''} 年订单`}
        open={purgeTarget !== null}
        onCancel={() => setPurgeTarget(null)}
        onOk={() => purgeTarget && purge.mutate(purgeTarget.year)}
        okText="确认物理删除"
        confirmLoading={purge.isPending}
        okButtonProps={{ danger: true, disabled: previewing || purgeBlocked || !purgeConfirmed }}
      >
        {purgeTarget && (
          <>
            {previewing && !purgePreview ? (
              <Text type="secondary">正在核对闸门…</Text>
            ) : purgeBlocked ? (
              <Alert
                type="error"
                showIcon
                title="当前不可清理"
                description={(purgePreview?.gaps ?? []).join('；')}
              />
            ) : (
              <>
                <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
                  <Descriptions.Item label="可清理（满 1 年且已结清）">{purgePreview?.eligible ?? 0} 条</Descriptions.Item>
                  <Descriptions.Item label="保留（未满 1 年或未结清）">{purgePreview?.kept ?? 0} 条</Descriptions.Item>
                </Descriptions>
                {purgePreview && purgePreview.keptReasons.length > 0 ? (
                  <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                    {purgePreview.keptReasons.join('；')}
                  </Text>
                ) : null}
                <Checkbox checked={purgeConfirmed} onChange={(e) => setPurgeConfirmed(e.target.checked)}>
                  我已导出台账并另存备份，确认删除这批订单及其付款凭证图片（不可恢复）
                </Checkbox>
              </>
            )}
          </>
        )}
      </Modal>
    </Card>
  );
}
